<template>
  <view class="v-form-uni-render">
    <!-- 调试信息：如果 fields 为空则显示 -->
    <view v-if="!renderFields || renderFields.length === 0" class="no-data">
      正在解析表单数据 (已接收: {{ fieldsCount }} 个组件)...
    </view>
    
    <view v-for="(field, index) in renderFields" :key="index" class="field-item">
      <!-- 1. Alert 提示组件 -->
      <view v-if="field.type === 'alert'" class="alert-box" :class="field.options.type || 'info'">
        <view class="alert-title">{{ field.options.title }}</view>
        <view class="alert-desc" v-if="field.options.description">{{ field.options.description }}</view>
      </view>

      <!-- 2. 栅格布局处理 (递归调用自身) -->
      <view v-else-if="field.type === 'grid'" class="grid-row">
        <view v-for="(col, colIdx) in field.cols" :key="colIdx" :style="{ flex: col.span / 24 }" class="grid-col">
          <v-form-uni-render :form-data="{ widgetList: col.widgetList }" :is-sub-form="true" @update-data="handleSubUpdate" />
        </view>
      </view>

      <!-- 3. 普通字段渲染 -->
      <view v-else-if="field.options && !field.options.hidden" class="form-item">
        <view class="label" :class="{ required: field.options.required }">
          {{ field.options.label }}
        </view>
        
        <view class="control">
          <!-- 单行文本 / 密码 / 数字 -->
          <input 
            v-if="['input', 'password', 'number'].includes(field.type)"
            v-model="internalFormData[field.options.name]"
            :password="field.type === 'password'"
            :type="field.type === 'number' ? 'number' : 'text'"
            :placeholder="field.options.placeholder"
            :disabled="disabled || field.options.disabled"
            class="uni-input"
            @input="emitUpdate"
          />

          <!-- 多行文本 -->
          <textarea 
            v-else-if="field.type === 'textarea'"
            v-model="internalFormData[field.options.name]"
            :placeholder="field.options.placeholder"
            :disabled="disabled || field.options.disabled"
            class="uni-textarea"
            auto-height
            @input="emitUpdate"
          />

          <!-- 选择器 (Select) -->
          <picker 
            v-else-if="field.type === 'select'"
            mode="selector"
            :range="field.options.optionList"
            range-key="label"
            @change="(e) => onPickerChange(e, field)"
            :disabled="disabled || field.options.disabled"
          >
            <view class="uni-input">
              {{ getPickerLabel(field) || field.options.placeholder || '请选择' }}
            </view>
          </picker>

          <!-- 文件上传 (简化版展示) -->
          <view v-else-if="field.type === 'file-upload'" class="file-upload-box">
            <view class="upload-btn" @click="handleFileUpload(field)" v-if="!disabled && !field.options.disabled">
              <text class="plus-icon">+</text>
              <text>上传文件</text>
            </view>
            <view class="file-list" v-if="internalFormData[field.options.name]">
              <view class="file-item-name" v-for="(file, fIdx) in parseFileList(internalFormData[field.options.name])" :key="fIdx">
                <text class="file-icon">📄</text>
                <text class="name">{{ file.name || '未知文件' }}</text>
              </view>
            </view>
            <view class="upload-tip" v-if="field.options.uploadTip">{{ field.options.uploadTip }}</view>
          </view>

          <!-- 日期选择 -->
          <picker 
            v-else-if="field.type === 'date'"
            mode="date"
            @change="(e) => onDateChange(e, field)"
            :disabled="disabled || field.options.disabled"
          >
            <view class="uni-input">
              {{ internalFormData[field.options.name] || field.options.placeholder || '请选择日期' }}
            </view>
          </picker>

          <!-- 开关 -->
          <switch 
            v-else-if="field.type === 'switch'"
            :checked="internalFormData[field.options.name]"
            @change="(e) => onSwitchChange(e, field)"
            :disabled="disabled || field.options.disabled"
          />

          <!-- 默认占位 -->
          <view v-else class="unsupported">
            暂不支持组件: {{ field.type }} ({{ field.options.label || '无标题' }})
          </view>
        </view>
      </view>
    </view>
  </view>
</template>

<script>
/**
 * VFormUniRender
 * 专门为移动端小程序/App设计的 VForm 渲染器，重命名以避免名称冲突
 */
export default {
  name: 'v-form-uni-render',
  props: {
    formData: {
      type: [Object, String],
      default: () => ({})
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isSubForm: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      renderFields: [],
      internalFormData: {},
      fieldsCount: 0
    }
  },
  watch: {
    formData: {
      handler(val) {
        this.parseFormConf(val);
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    parseFormConf(val) {
      if (!val) {
        this.renderFields = [];
        this.fieldsCount = 0;
        return;
      }
      
      let conf = val;
      if (typeof val === 'string') {
        try {
          const cleanStr = val.replace(/\\"/g, '"').replace(/\\n/g, '');
          conf = JSON.parse(cleanStr);
        } catch (e) {
          console.error('VFormUniRender: JSON解析失败', e);
          this.renderFields = [];
          return;
        }
      }
      
      // 优先支持 widgetList (你的打印数据中显示的结构)
      if (conf && conf.widgetList) {
        this.renderFields = conf.widgetList;
      } else if (conf && conf.fields) {
        this.renderFields = conf.fields;
      } else if (Array.isArray(conf)) {
        this.renderFields = conf;
      }
      
      this.fieldsCount = this.renderFields.length;
      this.initData(this.renderFields);
    },
    
    initData(fields) {
      if (!fields || !Array.isArray(fields)) return;
      
      fields.forEach(field => {
        if (field.type === 'grid' && field.cols) {
          field.cols.forEach(col => this.initData(col.widgetList));
        } else if (field.options && field.options.name) {
          const name = field.options.name;
          if (this.internalFormData[name] === undefined) {
            this.$set(this.internalFormData, name, field.options.defaultValue !== undefined ? field.options.defaultValue : '');
          }
        }
      });
    },

    onPickerChange(e, field) {
      const index = e.detail.value;
      const option = field.options.optionList[index];
      if (option) {
        this.$set(this.internalFormData, field.options.name, option.value);
        this.emitUpdate();
      }
    },

    onDateChange(e, field) {
      this.$set(this.internalFormData, field.options.name, e.detail.value);
      this.emitUpdate();
    },

    onSwitchChange(e, field) {
      this.$set(this.internalFormData, field.options.name, e.detail.value);
      this.emitUpdate();
    },

    getPickerLabel(field) {
      const value = this.internalFormData[field.options.name];
      if (!field.options.optionList) return '';
      const option = field.options.optionList.find(opt => opt.value === value);
      return option ? option.label : '';
    },

    parseFileList(val) {
      if (!val) return [];
      if (Array.isArray(val)) return val;
      try {
        return JSON.parse(val);
      } catch (e) {
        return [];
      }
    },

    handleFileUpload(field) {
      uni.showToast({ title: '文件上传功能开发中', icon: 'none' });
    },

    emitUpdate() {
      this.$emit('update-data', this.internalFormData);
    },

    handleSubUpdate(data) {
      Object.assign(this.internalFormData, data);
      this.emitUpdate();
    },

    getFormData() {
      return this.internalFormData;
    },

    validateForm() {
      let valid = true;
      this.renderFields.forEach(field => {
        if (field.options && field.options.required) {
          const val = this.internalFormData[field.options.name];
          if (val === undefined || val === null || val === '') {
            valid = false;
          }
        }
      });
      return valid;
    }
  }
}
</script>

<style scoped>
.v-form-uni-render {
  padding: 20rpx;
  background-color: #fff;
}
.no-data {
  padding: 40rpx;
  text-align: center;
  color: #999;
  font-size: 26rpx;
}
.field-item {
  margin-bottom: 30rpx;
}
.form-item {
  border-bottom: 1px solid #f5f5f5;
  padding-bottom: 20rpx;
}

/* Alert 样式 */
.alert-box {
  padding: 24rpx;
  border-radius: 12rpx;
  margin-bottom: 20rpx;
}
.alert-box.info { background-color: #e6f7ff; border: 1px solid #91d5ff; color: #1890ff; }
.alert-box.success { background-color: #f6ffed; border: 1px solid #b7eb8f; color: #52c41a; }
.alert-box.warning { background-color: #fffbe6; border: 1px solid #ffe58f; color: #faad14; }
.alert-box.error { background-color: #fff1f0; border: 1px solid #ffa39e; color: #f5222d; }
.alert-title { font-weight: bold; font-size: 28rpx; margin-bottom: 8rpx; }
.alert-desc { font-size: 24rpx; line-height: 1.6; }

/* 文件上传样式 */
.file-upload-box { padding: 10rpx 0; }
.upload-btn {
  width: 160rpx; height: 160rpx; border: 1px dashed #d9d9d9; background-color: #fafafa;
  display: flex; flex-direction: column; justify-content: center; align-items: center;
  border-radius: 12rpx; color: #666; font-size: 24rpx;
}
.plus-icon { font-size: 48rpx; margin-bottom: 8rpx; color: #999; }
.file-item-name {
  display: flex; align-items: center; background: #f9f9f9; padding: 12rpx 20rpx;
  border-radius: 8rpx; margin-bottom: 12rpx; font-size: 24rpx; color: #1890ff;
}
.file-icon { margin-right: 12rpx; }
.upload-tip { font-size: 22rpx; color: #999; margin-top: 12rpx; }

.label {
  font-size: 28rpx; color: #333; margin-bottom: 18rpx; font-weight: bold;
}
.label.required::before {
  content: '*'; color: #ff4d4f; margin-right: 8rpx;
}
.uni-input {
  height: 84rpx; line-height: 84rpx; font-size: 28rpx; background-color: #f8f8f8;
  padding: 0 24rpx; border-radius: 12rpx; color: #333;
}
.uni-textarea {
  width: 100%; min-height: 180rpx; font-size: 28rpx; background-color: #f8f8f8;
  padding: 24rpx; border-radius: 12rpx; box-sizing: border-box; color: #333;
}
.grid-row { display: flex; flex-wrap: wrap; margin: 0 -10rpx; }
.grid-col { padding: 0 10rpx; box-sizing: border-box; }
.unsupported {
  font-size: 24rpx; color: #999; background: #f8f8f8; padding: 20rpx;
  border-radius: 12rpx; border: 1px dashed #eee;
}
</style>
